import LooksThreeIcon from "@mui/icons-material/Looks3";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import { Box, Divider, Stack, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { makeStyles } from "@mui/styles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React, { useState } from "react";
import WarningDialog from "../../components/fundamentals/WarningDialog";
import { isAwardedBid, isClosedBid, isDeletedBid, isOpenBid } from "../../util/bid/bidCommon";
import { sortOnCaseSensitiveTexts } from "../../util/commonUtils";
import { FREEFORM_REASON_CHAR_LIMIT_FOR_BID, SELECT_HOURS_LIST, SELECT_MINUTES_LIST } from "../../util/constants";
import { TODAY } from "../../util/dateHelper";
import {
  convert_DateFns_To_Luxon_for_Station_TimeZone_at_Midnight,
  convert_DateFns_To_Luxon_for_Station_TimeZone_at_EndOfDay,
  convert_Luxon_To_DateFns,
} from "../../util/dateTimeFormatter";
import { SCHEDULE_END_DATE_CHANGE_MESSAGE } from "../../util/messageConstants";
import StarsSelectInput from "../fundamentals/StarsSelectInput";
import { isAwardedBidPastTheEndDate } from "../../util/bid/shiftBidUtils";

const useStyles = makeStyles((theme) => ({
  doubleDateInput: {
    "& .Mui-selected": {
      color: theme.palette.secondary.swaLightBlue,
    },
  },
  selectInput: {
    width: "180px",
  },
}));

const SetUpShiftBid = (props) => {
  const classes = useStyles();
  const [schedEndDateChangedDialog, setSchedEndDateChangedDialog] = useState(false);

  const handleScheduleDialogClose = () => {
    setSchedEndDateChangedDialog(false);
  };

  const handleSchedDatesChange = (newValue) => {
    const newStartDate = convert_DateFns_To_Luxon_for_Station_TimeZone_at_Midnight(newValue[0], props.bid.station);
    const newEndDate = convert_DateFns_To_Luxon_for_Station_TimeZone_at_EndOfDay(newValue[1], props.bid.station);
    if (
      (isOpenBid(props.bid.status) || isClosedBid(props.bid.status) || isAwardedBid(props.bid.status)) &&
      newEndDate.toMillis() !== props.schedDates[1].toMillis()
    ) {
      setSchedEndDateChangedDialog(true);
    }
    props.setSchedDates([newStartDate, newEndDate]);
  };

  return (
    <>
      <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
        <Grid item xs={12} sx={{ padding: "10px" }}>
          <Typography variant="h6" sx={{ textAlign: "left", margin: "10px", fontWeight: "bold" }}>
            {props.bid.station} | {props.bid.workgroup}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="left">
            <Box sx={{ display: "flex", alignItems: "center", margin: "50px" }}>
              <LooksOneIcon color="primary" fontSize="large" />
            </Box>
            <Stack direction="column">
              <Box m={1} sx={{ textAlign: "left", marginTop: "20px" }}>
                <Typography variant="h5">Bid Effective Dates</Typography>
                <Typography variant="text">Select when this bid will effectively start and end.</Typography>
              </Box>
              <Box m={1} sx={{ marginBottom: "20px" }}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  localeText={{ start: "Bid Start Date", end: "Tentative Bid End Date" }}
                >
                  <MobileDateRangePicker
                    value={[
                      convert_Luxon_To_DateFns(props.schedDates[0]),
                      convert_Luxon_To_DateFns(props.schedDates[1]),
                    ]}
                    onChange={(newValue) => handleSchedDatesChange(newValue)}
                    renderInput={(startProps, endProps) => {
                      return (
                        <React.Fragment>
                          <TextField
                            {...startProps}
                            error={props.schedStartDateError !== "" || props.schedDatesErrors !== ""}
                            helperText={props.schedStartDateError || props.schedDatesErrors}
                            inputProps={{
                              ...startProps.inputProps,
                              placeholder: "",
                              disabled:
                                startProps.disabled ||
                                isOpenBid(props.bid.status) ||
                                isClosedBid(props.bid.status) ||
                                isAwardedBid(props.bid.status),
                            }}
                            disabled={
                              startProps.disabled ||
                              isOpenBid(props.bid.status) ||
                              isClosedBid(props.bid.status) ||
                              isAwardedBid(props.bid.status)
                            }
                          />
                          <Box sx={{ mx: 2 }}> to </Box>
                          <TextField
                            {...endProps}
                            error={props.schedEndDateError !== "" || props.schedDatesErrors !== ""}
                            helperText={props.schedEndDateError}
                            inputProps={{ ...endProps.inputProps, placeholder: "" }}
                          />
                        </React.Fragment>
                      );
                    }}
                    className={classes.doubleDateInput}
                    showToolbar={true}
                    disabled={
                      isDeletedBid(props.bid.status) || isAwardedBidPastTheEndDate(props.bid, props.schedDates[1])
                    }
                    minDate={convert_Luxon_To_DateFns(
                      isAwardedBid(props.bid.status)
                        ? DateTime.max(TODAY(props.bid.station), props.bid.schedStartDate)
                        : TODAY(props.bid.station)
                    )}
                    showDaysOutsideCurrentMonth={true}
                    inputFormat="iii MMM dd, yyyy"
                    disableMaskedInput={true}
                    closeOnSelect={true}
                    shouldDisableDate={(_, position) =>
                      (isOpenBid(props.bid.status) ||
                        isClosedBid(props.bid.status) ||
                        isAwardedBid(props.bid.status)) &&
                      position === "start"
                    }
                  />
                </LocalizationProvider>
              </Box>
            </Stack>
          </Stack>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Stack direction="row" justifyContent="left">
            <Box sx={{ display: "flex", alignItems: "center", margin: "50px" }}>
              <LooksTwoIcon color="primary" fontSize="large" />
            </Box>
            <Stack direction="column">
              <Box m={1} sx={{ textAlign: "left", marginTop: "20px" }}>
                <Typography variant="h5">Bid Window</Typography>
                <Typography variant="text">Select which day and time this bid will open and close.</Typography>
              </Box>
              <Stack direction="row">
                <Box m={1} sx={{ marginBottom: "20px" }}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    localeText={{ start: "Bid Open Date", end: "Bid Close Date" }}
                  >
                    <MobileDateRangePicker
                      value={[
                        convert_Luxon_To_DateFns(props.windowDates[0]),
                        convert_Luxon_To_DateFns(props.windowDates[1]),
                      ]}
                      onChange={(newValue) => {
                        props.setWindowDates([
                          convert_DateFns_To_Luxon_for_Station_TimeZone_at_Midnight(newValue[0], props.bid.station),
                          convert_DateFns_To_Luxon_for_Station_TimeZone_at_Midnight(newValue[1], props.bid.station),
                        ]);
                      }}
                      renderInput={(startProps, endProps) => (
                        <React.Fragment>
                          <TextField
                            {...startProps}
                            error={props.windowDatesErrors !== ""}
                            helperText={props.windowDatesErrors}
                            inputProps={{
                              ...startProps.inputProps,
                              placeholder: "",
                              disabled: startProps.disabled || isOpenBid(props.bid.status),
                            }}
                            disabled={startProps.disabled || isOpenBid(props.bid.status)}
                          />
                          <Box sx={{ mx: 2 }}> to </Box>
                          <TextField
                            {...endProps}
                            error={props.windowDatesErrors !== ""}
                            inputProps={{ ...endProps.inputProps, placeholder: "" }}
                          />
                        </React.Fragment>
                      )}
                      className={classes.doubleDateInput}
                      showToolbar={true}
                      disabled={
                        isDeletedBid(props.bid.status) ||
                        isClosedBid(props.bid.status) ||
                        isAwardedBid(props.bid.status)
                      }
                      minDate={convert_Luxon_To_DateFns(TODAY(props.bid.station))}
                      showDaysOutsideCurrentMonth={true}
                      inputFormat="iii MMM dd, yyyy"
                      disableMaskedInput={true}
                      closeOnSelect={true}
                      shouldDisableDate={(_, position) => isOpenBid(props.bid.status) && position === "start"}
                    />
                  </LocalizationProvider>
                </Box>
                <Box m={1}>
                  <StarsSelectInput
                    label="Start/End Time Hr"
                    menuMessage="Select Start/End Time Hr"
                    value={props.windowDateHr}
                    allOptions={SELECT_HOURS_LIST}
                    sortComparator={sortOnCaseSensitiveTexts}
                    onChange={(event) => props.setWindowDateHr(event)}
                    errors={props.windowDateTimeErrors}
                    className={classes.selectInput}
                    disabled={
                      props.windowDates[0] === null ||
                      props.windowDates[1] === null ||
                      isDeletedBid(props.bid.status) ||
                      isClosedBid(props.bid.status) ||
                      isOpenBid(props.bid.status) ||
                      isAwardedBid(props.bid.status)
                    }
                  />
                </Box>
                <Box m={1}>
                  <StarsSelectInput
                    label="Start/End Time Min"
                    menuMessage="Select Start/End Time Min"
                    value={props.windowDateMin}
                    allOptions={SELECT_MINUTES_LIST}
                    sortComparator={sortOnCaseSensitiveTexts}
                    onChange={(event) => props.setWindowDateMin(event)}
                    errors={props.windowDateTimeErrors !== "" ? " " : ""}
                    className={classes.selectInput}
                    disabled={
                      props.windowDates[0] === null ||
                      props.windowDates[1] === null ||
                      isDeletedBid(props.bid.status) ||
                      isClosedBid(props.bid.status) ||
                      isOpenBid(props.bid.status) ||
                      isAwardedBid(props.bid.status)
                    }
                  />
                </Box>
              </Stack>
            </Stack>
          </Stack>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Stack direction="row" justifyContent="left">
            <Box sx={{ display: "flex", alignItems: "center", margin: "50px" }}>
              <LooksThreeIcon color="primary" fontSize="large" />
            </Box>
            <Stack direction="column">
              <Box m={1} sx={{ textAlign: "left", marginTop: "20px" }}>
                <Typography variant="h5">Bid Name</Typography>
                <Typography variant="text">
                  You can enter a custom name for this shift bid. It is not required.
                </Typography>
              </Box>
              <Box m={1} sx={{ textAlign: "left", marginBottom: "20px" }}>
                <TextField
                  sx={{ width: "500px" }}
                  label="Bid Name"
                  variant="outlined"
                  value={props.bidName}
                  onChange={props.setBidNameChange}
                  disabled={
                    isDeletedBid(props.bid.status) ||
                    isOpenBid(props.bid.status) ||
                    isClosedBid(props.bid.status) ||
                    isAwardedBid(props.bid.status)
                  }
                  error={props.bidNameErrors !== ""}
                  helperText={props.bidNameErrors}
                />
                {props.bidName.length > FREEFORM_REASON_CHAR_LIMIT_FOR_BID ? (
                  <Box sx={{ color: "primary.swaRed", textAlign: "right" }}>
                    {props.bidName.length} / {FREEFORM_REASON_CHAR_LIMIT_FOR_BID}
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "right" }}>
                    {props.bidName.length} / {FREEFORM_REASON_CHAR_LIMIT_FOR_BID}
                  </Box>
                )}
              </Box>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
      <WarningDialog
        isDialogOpen={schedEndDateChangedDialog}
        handleClickDialogClose={handleScheduleDialogClose}
        header="Warning"
        message={SCHEDULE_END_DATE_CHANGE_MESSAGE}
        severity={"warning"}
      />
    </>
  );
};

SetUpShiftBid.propTypes = {
  bid: PropTypes.object.isRequired,
  schedDates: PropTypes.array.isRequired,
  setSchedDates: PropTypes.func.isRequired,
  schedDatesErrors: PropTypes.string.isRequired,
  schedStartDateError: PropTypes.string.isRequired,
  schedEndDateError: PropTypes.string.isRequired,
  windowDates: PropTypes.array.isRequired,
  setWindowDates: PropTypes.func.isRequired,
  windowDatesErrors: PropTypes.string.isRequired,
  windowDateHr: PropTypes.string.isRequired,
  setWindowDateHr: PropTypes.func.isRequired,
  windowDateMin: PropTypes.string.isRequired,
  setWindowDateMin: PropTypes.func.isRequired,
  windowDateTimeErrors: PropTypes.string.isRequired,
  bidName: PropTypes.string.isRequired,
  bidNameErrors: PropTypes.string.isRequired,
  setBidNameChange: PropTypes.func.isRequired,
};

export default SetUpShiftBid;
